import api, { ValidateAxiosError } from '@/modules/api.service';
import { CreateCandidatoDto, ReadCandidatoDto, UpdateCandidatoDto } from '@/api.types';
import { Endpoints } from '@/shared/constants/endpoints';
import { SearchDto } from '@/shared/types/search.dto';

export class CandidatoService {
  async create(obj: CreateCandidatoDto): Promise<number> {
    try {
      return (await api.post(Endpoints.CANDIDATO, obj)).data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao realizar inscrição.');
    }
  }

  async createPublic(obj: CreateCandidatoDto): Promise<number> {
    try {
      return (await api.post(Endpoints.CANDIDATO + '/public', obj)).data;
    } catch (error: any) {

      // Se o erro contém uma lista de erros de validação, lançar o erro completo sem processar
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors) && error.response.data.errors.length > 0) {
        throw error; // Lançar o erro para ser processado no componente
      }
      // Para outros erros, usar ValidateAxiosError
      throw ValidateAxiosError(error, 'Falha ao realizar inscrição.');
    }
  }

  async findOne(id: number): Promise<ReadCandidatoDto> {
    try {
      const response = await api.get(Endpoints.CANDIDATO + '/' + id);
      return response.data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao buscar a inscrição.');
    }
  }

  async findOnePublic(id: number): Promise<ReadCandidatoDto> {
    try {
      const response = await api.get(Endpoints.CANDIDATO + '/public/' + id);
      return response.data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao buscar a inscrição.');
    }
  }

  async update(id: number, dto: UpdateCandidatoDto): Promise<void> {
    try {
      return (await api.put(Endpoints.CANDIDATO + '/' + id, dto)).data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao atualizar inscrição.');
    }
  }

  async updatePublic(id: number, dto: UpdateCandidatoDto): Promise<void> {
    try {
      return (await api.put(Endpoints.CANDIDATO + '/public/' + id, dto)).data;
    } catch (error: any) {
      // Se o erro contém uma lista de erros de validação, lançar o erro completo sem processar
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        throw error; // Lançar o erro para ser processado no componente
      }
      // Para outros erros, usar ValidateAxiosError
      throw ValidateAxiosError(error, 'Falha ao atualizar inscrição.');
    }
  }

  async findAll(conditions: SearchDto, processoSeletivoId?: number): Promise<[ReadCandidatoDto[], number]> {
    try {
      const params: any = {
        skip: conditions.skip,
        take: conditions.take,
        where: conditions.where ?? undefined,
        orderBy: conditions.orderBy ?? undefined,
      };
      if (processoSeletivoId) {
        params.processoSeletivoId = processoSeletivoId;
      }
      const response = await api.get(Endpoints.CANDIDATO, { params });
      return response.data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao listar candidatos.');
    }
  }

  async createPublicRascunho(processoSeletivoId: number): Promise<number> {
    const response = await api.post<{ candidatoId: number }>(Endpoints.CANDIDATO + '/public/rascunho', {
      processoSeletivoId,
    });
    return response.data.candidatoId;
  }

  async uploadFile(
    file: File,
    tipo:
      | 'documentacao_indigena'
      | 'laudo_medico'
      | 'documento_identificacao'
      | 'comprovante_residencia'
      | 'documento_estudo'
      | 'documentos_complementares'
      | 'prova',
    candidatoId: number,
  ): Promise<string> {
    try {
      if (!file) {
        throw new Error('Arquivo não fornecido');
      }
      if (!candidatoId || candidatoId <= 0) {
        throw new Error('candidatoId é obrigatório para envio de arquivo.');
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('tipo', tipo);
      formData.append('candidatoId', candidatoId.toString());

      const response = await api.post(Endpoints.CANDIDATO + '/upload', formData);

      if (!response.data?.fileId) {
        throw new Error('Resposta do servidor não contém fileId');
      }

      return response.data.fileId;
    } catch (error: any) {
      console.error('Erro no upload:', error);
      if (error.response) {
        console.error('Resposta do erro:', error.response.data);
        console.error('Status:', error.response.status);
      }
      ValidateAxiosError(error, 'Falha ao fazer upload do arquivo.');
      throw error;
    }
  }

  async downloadFile(fileId: string): Promise<Blob> {
    try {
      const response = await api.get(Endpoints.CANDIDATO + '/file/' + fileId, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao baixar arquivo.');
    }
  }

  async delete(id: number): Promise<void> {
    try {
      await api.delete(Endpoints.CANDIDATO + '/' + id);
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao excluir candidato.');
    }
  }

  async vincularArquivosLegados(processoSeletivoId?: number): Promise<{
    vinculados: number;
    jaCorretos: number;
    formatoInvalido: number;
    erros: string[];
  }> {
    const params = Number.isFinite(processoSeletivoId as number) ? { processoSeletivoId } : {};
    const response = await api.post<{
      vinculados: number;
      jaCorretos: number;
      formatoInvalido: number;
      erros: string[];
    }>(Endpoints.CANDIDATO + '/vincular-arquivos-legados', {}, { params });
    return response.data;
  }

  async listarArquivosParaChecagem(processoSeletivoId: number): Promise<{ id: string; filename: string; caminhoServidor: string | null; candidatoId: number | null; candidatoNome: string | null }[]> {
    if (!Number.isFinite(processoSeletivoId)) {
      throw new Error('processoSeletivoId é obrigatório para listar arquivos para checagem.');
    }
    const response = await api.get<{ id: string; filename: string; caminhoServidor: string | null; candidatoId: number | null; candidatoNome: string | null }[]>(
      Endpoints.CANDIDATO + '/arquivos/checagem',
      { params: { processoSeletivoId } },
    );
    return response.data;
  }

  async arquivoExisteNoServidor(caminhoServidor: string): Promise<boolean> {
    const response = await api.get<{ exists: boolean }>(Endpoints.CANDIDATO + '/arquivo-existe', {
      params: { caminhoServidor },
    });
    return response.data.exists;
  }

  async arquivoExisteNoServidorPorId(fileId: string): Promise<{ exists: boolean; caminhoServidor?: string | null }> {
    const response = await api.get<{ exists: boolean; caminhoServidor?: string | null }>(
      Endpoints.CANDIDATO + '/arquivo-existe',
      { params: { fileId } },
    );
    return response.data;
  }

  async getPromoverPreview(candidatoId: number) {
    try {
      const response = await api.get(`${Endpoints.CANDIDATO}/${candidatoId}/promover-estagiario/preview`);
      return response.data;
    } catch (error) {
      throw ValidateAxiosError(error, 'Falha ao carregar dados para promoção.');
    }
  }

  async promoverEstagiario(
    candidatoId: number,
    dto: {
      tipo_estagio: number;
      email: string;
      data_inicio_estagio: string;
      data_fim_estagio?: string;
      fim_semana?: boolean;
    },
  ): Promise<{ pessoaId: string }> {
    try {
      const response = await api.post(`${Endpoints.CANDIDATO}/${candidatoId}/promover-estagiario`, dto);
      return response.data;
    } catch (error) {
      throw ValidateAxiosError(error, 'Falha ao promover candidato a estagiário.');
    }
  }
}

